import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/stores/authStore";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components";
import { tx } from "@/i18n/tx";

const WARN_BEFORE_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart", "wheel"];
const LS_KEY = "arcquan.session.lastRefresh"; // shared across tabs so only one tab rotates the refresh token
const log = (event, extra = {}) => console.info(`[session] ${new Date().toISOString()} ${event}`, extra);

// Keeps the session alive while the user is active and warns before the idle limit.
// The server is the authority: /auth/refresh validates idle + max limits and rotates tokens.
export function SessionManager() {
  const { ctx, logout } = useAuth();
  const [session, setSession] = useState(null);
  const [warn, setWarn] = useState(false);
  const [left, setLeft] = useState(0);
  const lastActivity = useRef(Date.now());
  const lastRefresh = useRef(Date.now());
  const refreshing = useRef(false);

  const endSession = useCallback(async (msg, reason) => {
    log("logout", { reason, message: msg });
    toast.error(msg, { duration: 10000 });
    await logout();
  }, [logout]);

  const extend = useCallback(async (why = "auto") => {
    if (refreshing.current) return true;
    const other = Number(localStorage.getItem(LS_KEY) || 0);
    if (why === "auto" && Date.now() - other < 60 * 1000) { lastRefresh.current = other; log("refresh skipped (another tab just refreshed)"); return true; }
    refreshing.current = true;
    try {
      const r = await api.post("/auth/refresh");
      setSession(r.data.data.session);
      lastRefresh.current = Date.now();
      localStorage.setItem(LS_KEY, String(lastRefresh.current));
      setWarn(false);
      log("refresh ok", { why, idle_expires_at: r.data.data.session?.idle_expires_at, max_expires_at: r.data.data.session?.max_expires_at });
      return true;
    } catch (e) {
      const status = e?.response?.status;
      log("refresh failed", { why, status, message: e?.response?.data?.error?.message });
      if (status === 401) await endSession(e?.response?.data?.error?.message || tx("Sessionen har löpt ut – logga in igen."), "refresh_401");
      return false;
    } finally { refreshing.current = false; }
  }, [endSession]);

  useEffect(() => {
    if (!ctx) return undefined;
    api.get("/auth/session").then((r) => { setSession(r.data.data); log("policy loaded", r.data.data); }).catch(() => {});
    // Only genuine user interaction counts as activity – background polling (notifications etc.) must not keep a session alive.
    const bump = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true, capture: true }));
    const onStorage = (e) => { if (e.key === LS_KEY && e.newValue) { lastRefresh.current = Number(e.newValue); setWarn(false); api.get("/auth/session").then((r) => setSession(r.data.data)).catch(() => {}); } };
    window.addEventListener("storage", onStorage);
    return () => { ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump, { capture: true })); window.removeEventListener("storage", onStorage); };
  }, [ctx]);

  useEffect(() => {
    if (!ctx || !session) return undefined;
    const tick = async () => {
      const now = Date.now();
      const idleEnd = new Date(session.idle_expires_at).getTime();
      const maxEnd = new Date(session.max_expires_at).getTime();
      if (now >= maxEnd) return endSession(tx("Sessionen har nått sin maximala längd – logga in igen."), "max_session");
      const activeRecently = now - lastActivity.current < 5 * 60 * 1000;
      const accessAgeMs = now - Math.max(lastRefresh.current, Number(localStorage.getItem(LS_KEY) || 0));
      // Active user: rotate well before the idle limit (and before the access token expires) so the server records activity.
      if (activeRecently && (accessAgeMs > 4 * 60 * 1000 || idleEnd - now < WARN_BEFORE_MS + 60 * 1000)) {
        if (document.visibilityState !== "hidden" && (await extend("activity"))) return;
      }
      if (idleEnd - now <= WARN_BEFORE_MS && idleEnd > now) { if (!warn) log("idle warning shown"); setWarn(true); setLeft(Math.max(0, Math.ceil((idleEnd - now) / 1000))); }
      else if (now >= idleEnd) return endSession(tx("Sessionen har löpt ut på grund av inaktivitet."), "idle");
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [ctx, session, warn, extend, endSession]);

  useEffect(() => {
    if (!warn) return undefined;
    const iv = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(iv);
  }, [warn]);

  if (!ctx) return null;
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <Dialog open={warn} onOpenChange={() => {}}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm rounded-lg" data-testid="session-warning-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{tx("Du loggas snart ut")}</DialogTitle>
          <DialogDescription>{tx("På grund av inaktivitet avslutas sessionen om")} <span className="font-semibold tabular-nums" data-testid="session-warning-countdown">{mm}:{ss}</span>.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => logout()} data-testid="session-logout-now">{tx("Logga ut")}</Button>
          <Button onClick={() => extend("user")} data-testid="session-stay-logged-in">{tx("Fortsätt vara inloggad")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
